import { useMemo, useState, useEffect } from 'react'

import { useTheme } from '@chakra-ui/react'
import { Select2 } from '@verbit-ai/verbit-ui-library'

import { useConfig } from 'src/contexts/ConfigurationContext'
import { useTranscriptMetadata } from 'src/contexts/Transcript/MetadataContext'
import { isChromakeyModeActive } from 'src/utils/env'
import { useTranscriptViewer } from 'src/contexts/TranscriptViewerProvider'
import { TranscriptSpeaker, TranscriptParagraph, Select2SpeakerPresentation } from 'src/models/Transcript'
import { useTranscriptClient, useTranscriptSpeakers } from 'src/contexts/Transcript/TranscriptContext'
import { useEditor } from 'src/contexts/EditorContext'
import { usePrevious } from 'src/hooks/usePrevious'
import { SpeakerColours } from 'src/services/SpeakerColour'
import { analyticsService } from 'src/services/AnalyticsService'

import * as S from './styles'
import { UNIDENTIFIED_SPEAKER_ID } from './constants'
import { getAuthorName } from './getAuthorName'

type ParagraphAuthorProps = {
    paragraph: TranscriptParagraph
    isDiarizationSupported: boolean
    author?: TranscriptSpeaker
}

const DEFAULT_FALLBACK_COLOR = 'lightgrey'

// map speaker collection to Select2 component
const ITEM_ID_KEY = 'id'
const ITEM_TITLE_KEY = 'name'
const ITEM_SUBTITLE_KEY = 'role'
const ITEM_COLOR_KEY = 'color'

const UnidentifieableSpeakerItem: Select2SpeakerPresentation = {
    [ITEM_ID_KEY]: UNIDENTIFIED_SPEAKER_ID,
    [ITEM_TITLE_KEY]: 'Not sure',
    [ITEM_SUBTITLE_KEY]: 'You can always address it later',
    [ITEM_COLOR_KEY]: '#7396BA',
}

export const ParagraphAuthor = (props: ParagraphAuthorProps) => {
    const { paragraph, author, isDiarizationSupported } = props
    const [{ isShowSpeakerColorCodeEnabled }] = useConfig()
    const transcriptClient = useTranscriptClient()
    const metadata = useTranscriptMetadata()
    const { isSpeakerEditAuthorised } = useEditor()
    const { exitAutoScroll } = useTranscriptViewer()

    const paragraphContainsEditableText = paragraph.content.some((contentEntry) => contentEntry.isEditable)
    const canSpeakerBeEdited = isSpeakerEditAuthorised && paragraphContainsEditableText
    const [isSpeakersSelectionOpen, setIsSpeakersSelectionOpen] = useState(false)
    const prevIsSpeakersSelectionOpen = usePrevious(isSpeakersSelectionOpen)

    // log analytics event when speakers selection is opened or closed
    useEffect(() => {
        // Only log if the value actually changed
        if (prevIsSpeakersSelectionOpen !== undefined && prevIsSpeakersSelectionOpen !== isSpeakersSelectionOpen) {
            if (isSpeakersSelectionOpen) {
                analyticsService.track('Speaker selection opened', {})
            } else {
                analyticsService.track('Speaker selection closed', {})
            }
        }
    }, [isSpeakersSelectionOpen, prevIsSpeakersSelectionOpen])

    const speakers = useTranscriptSpeakers()

    // check for existing transcript speakers and assign colors to them
    const allSpeakers: Select2SpeakerPresentation[] = useMemo(() => {
        if (!speakers || !isSpeakersSelectionOpen) return []

        const speakerItems: Select2SpeakerPresentation[] = speakers
            .map((speaker) => ({
                ...speaker,
                name: speaker.name || '>>',
                role: '',
                color: SpeakerColours.instance.getColour(speaker.id ?? ''),
            }))
            .reverse()

        return [...speakerItems, UnidentifieableSpeakerItem]
    }, [speakers, isSpeakersSelectionOpen])

    const authorName = getAuthorName({
        author,
        shouldSuppressSpeakerName: metadata?.suppressSpeakerName,
        isDiarizationSupported,
        viewMode: metadata?.viewMode ?? null,
    })
    const authorColor = author ? SpeakerColours.instance.getColour(author.id ?? '') : DEFAULT_FALLBACK_COLOR

    const {
        textColorIndex,
        palette: {
            transcript: { speakerColors },
        },
    } = useTheme()
    const textColor = speakerColors[textColorIndex]

    const onSelectPopoverOpen = (isOpen: boolean) => {
        if (isOpen) {
            exitAutoScroll()
        }
        setIsSpeakersSelectionOpen(isOpen)
    }

    const onItemSelected = <T extends Record<string, unknown>>(retItem: T) => {
        const paragraphId = paragraph.id
        const speakerId = retItem[ITEM_ID_KEY] as string

        transcriptClient.setParagraph(
            {
                paragraphId,
                paragraph: {
                    speakerId,
                },
            },
            { origin: 'speaker_box' },
        )

        analyticsService.track('Speaker item selected', retItem)
    }

    const onNewItemAdded = (speakerName: string) => {
        const paragraphId = paragraph.id
        const speakerId = crypto.randomUUID()

        transcriptClient.addSpeaker(
            {
                id: speakerId,
                speaker: {
                    name: speakerName,
                },
            },
            { origin: 'speaker_box' },
        )

        analyticsService.track('New speaker added', { paragraphId, speakerId, speakerName })
    }

    const onItemChanged = <T extends Record<string, unknown>>(retItem: T) => {
        const speakerId = String((retItem as Record<string, unknown>)[ITEM_ID_KEY])
        const speakerName = String((retItem as Record<string, unknown>)[ITEM_TITLE_KEY])

        transcriptClient.editSpeaker(
            {
                id: speakerId,
                speaker: {
                    name: speakerName,
                },
            },
            { origin: 'speaker_box' },
        )

        analyticsService.track('Speaker name edited', { speakerId, speakerName })
    }

    const filterItemsFn = (item: Select2SpeakerPresentation, searchText: string) => {
        // always include the unidentified speaker item when searching
        if (item[ITEM_ID_KEY] === UNIDENTIFIED_SPEAKER_ID) return true

        const matchesQuery = String(item[ITEM_TITLE_KEY] ?? '')
            .toLowerCase()
            .includes((searchText || '').toLowerCase())

        return matchesQuery
    }

    const shouldAllowItemEdit = (editableItem: { [x: string]: any }) => editableItem[ITEM_ID_KEY] !== UNIDENTIFIED_SPEAKER_ID
    const isTitleColourIconEnabled =
        !isChromakeyModeActive && (Boolean(isShowSpeakerColorCodeEnabled) || isSpeakersSelectionOpen) && metadata?.viewMode === 'job'

    return (
        <S.Speaker $color={textColor} $isTextEditingAvailable={canSpeakerBeEdited}>
            <Select2
                itemIdKey={ITEM_ID_KEY}
                itemTitleKey={ITEM_TITLE_KEY}
                itemSubtitleKey={ITEM_SUBTITLE_KEY}
                itemColorKey={ITEM_COLOR_KEY}
                collectionValues={allSpeakers}
                searchInputPlaceholder="Add or search speaker"
                addButtonLabel="Add Speaker"
                triggerElementText={authorName}
                triggerElementColour={authorColor}
                triggerElementButtonBgColor="transparent"
                onSelectPopoverOpen={onSelectPopoverOpen}
                onItemSelected={onItemSelected}
                onNewItemAdded={onNewItemAdded}
                onItemChanged={onItemChanged}
                isTitleColourIconEnabled={isTitleColourIconEnabled}
                shouldAllowItemEdit={shouldAllowItemEdit}
                filterItemsFn={filterItemsFn}
                isReadOnlyMode={!canSpeakerBeEdited}
            />
        </S.Speaker>
    )
}
